import { useCallback, useEffect, useState } from 'react'
import { groupForClassification, suggestedCategoryFor } from '@domain/finance'
import type { Category, MerchantGroup, Transaction } from '@domain/finance'
import type { Merchant } from '@domain/product'
import { useFinanceRepositories } from './finance-context'

/** Um grupo com o que a casa já decidiu antes para esta mesma chave. */
export type GroupWithSuggestion = MerchantGroup & { suggestedCategoryId: string | null }

export type ClassificationState =
  | { status: 'loading' }
  | { status: 'loaded'; groups: GroupWithSuggestion[]; categories: Category[] }
  | { status: 'failed' }

/**
 * Os grupos a classificar e as categorias para escolher.
 *
 * **Uma leitura só, na abertura.** A lista tem milhares de documentos, e relê-la
 * a cada classificação seria pagar tudo de novo para tirar uma linha. Quem
 * classifica um grupo o remove daqui em memória — `remova` —, que é exatamente
 * o que a releitura devolveria.
 */
export function useClassification(): ClassificationState & { remova: (key: string) => void } {
  const { transactionRepository, categoryRepository, productRepository } = useFinanceRepositories()
  const [state, setState] = useState<ClassificationState>({ status: 'loading' })

  useEffect(() => {
    let live = true
    void Promise.all([
      transactionRepository.listUncategorized(),
      categoryRepository.list(),
      productRepository.listMerchants(),
    ])
      .then(([transactions, categories, merchants]: [Transaction[], Category[], Merchant[]]) => {
        if (live) {
          setState({
            status: 'loaded',
            groups: groupForClassification(transactions).map((g) => ({
              ...g,
              suggestedCategoryId: suggestedCategoryFor(g.key, merchants),
            })),
            categories,
          })
        }
      })
      .catch((erro: unknown) => {
        console.error('useClassification: leitura falhou', erro)
        if (live) setState({ status: 'failed' })
      })
    return () => {
      live = false
    }
  }, [transactionRepository, categoryRepository, productRepository])

  const remova = useCallback((key: string) => {
    setState((atual) =>
      atual.status === 'loaded'
        ? { ...atual, groups: atual.groups.filter((g) => g.key !== key) }
        : atual,
    )
  }, [])

  return { ...state, remova }
}
